import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'

const SIDEBAR_STORAGE_KEY = 'admin-sidebar-collapsed'

interface LayoutContextValue {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  isMobile: boolean
  sidebarOpen: boolean // For mobile overlay
  setSidebarOpen: (open: boolean) => void
}

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined)

interface LayoutProviderProps {
  children: ReactNode
}

export function LayoutProvider({ children }: LayoutProviderProps) {
  // Initialize from localStorage
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    return stored === 'true'
  })
  
  const [sidebarOpen, setSidebarOpen] = useState(false) // Mobile overlay state
  const [isMobile, setIsMobile] = useState(false)

  // Handle responsive detection
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    const checkMobile = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768)
      }, 150)
    }
    
    // WHY: Set initial value immediately (no debounce on mount)
    setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', checkMobile)
    
    return () => {
      window.removeEventListener('resize', checkMobile)
      clearTimeout(timeoutId)
    }
  }, [])

  // Close mobile sidebar when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false)
    }
  }, [isMobile])

  // Persist sidebar collapsed state
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed)
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed))
  }, [])

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(prev => !prev)
    } else {
      setSidebarCollapsedState(prev => {
        const next = !prev
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
        return next
      })
    }
  }, [isMobile])

  /* WHY: Memoize so consumers only re-render when a layout value actually changes,
     not on every parent re-render (route change, auth update, etc.) */
  const value = useMemo<LayoutContextValue>(() => ({
    sidebarCollapsed,
    toggleSidebar,
    setSidebarCollapsed,
    isMobile,
    sidebarOpen,
    setSidebarOpen,
  }), [sidebarCollapsed, toggleSidebar, setSidebarCollapsed, isMobile, sidebarOpen, setSidebarOpen])

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  )
}

export function useLayout() {
  const context = useContext(LayoutContext)
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider')
  }
  return context
}

export { LayoutContext }
